import { Entity, EntityItem, Service, CustomAttributeType } from "electrodb";
import type { DynamoDBClient } from "@aws-sdk/client-dynamodb";
import { DynamoDBDocumentClient } from "@aws-sdk/lib-dynamodb";
import { TenantId, SiteId, AssetId, CameraId } from "@visioni/a3s-schema";
import { type ISODateString } from "@visioni/a3s-codecs";

const tenantIdAttribute = {
  type: CustomAttributeType<TenantId>("string"),
  required: true as const,
  set: (value?: TenantId) => (value ? TenantId.toString(value) : undefined),
  get: (value?: string) => (value ? TenantId.fromString(value) : undefined),
};

const siteIdAttribute = {
  type: CustomAttributeType<SiteId>("string"),
  required: true as const,
  set: (value?: SiteId) => (value ? SiteId.toString(value) : undefined),
  get: (value?: string) => (value ? SiteId.fromString(value) : undefined),
};

const assetIdAttribute = {
  type: CustomAttributeType<AssetId>("string"),
  required: true as const,
  set: (value?: AssetId) => (value ? AssetId.toString(value) : undefined),
  get: (value?: string) => (value ? AssetId.fromString(value) : undefined),
};

const cameraIdAttribute = {
  type: CustomAttributeType<CameraId>("string"),
  required: true as const,
  set: (value?: CameraId) => (value ? CameraId.toString(value) : undefined),
  get: (value?: string) => (value ? CameraId.fromString(value) : undefined),
};

const dateAttribute = {
  type: CustomAttributeType<ISODateString>("string"),
  required: true as const,
};

/**
 * ElectroDB entity for snapshot metadata.
 *
 * Key design:
 * - PK: SNAPSHOT#{tenant}#{site}#{asset}#{camera} (camera identifier)
 * - SK: {ISO8601_timestamp} (enables time-range queries and "get latest" with limit 1, descending)
 *
 * Note: timestamp is stored as ISO8601 string (not Date) because it's a composite
 * attribute in the sort key, and ElectroDB requires composite attributes to be
 * primitive types that can be used directly in DynamoDB key conditions.
 */
export const SnapshotEntity = new Entity({
  model: {
    service: "a3s-media",
    entity: "Snapshot",
    version: "1",
  },
  attributes: {
    tenant: tenantIdAttribute,
    site: siteIdAttribute,
    asset: assetIdAttribute,
    camera: cameraIdAttribute,
    timestamp: dateAttribute,
    snapshotKey: { type: "string", required: true }, // S3 key for JPEG
    createdAt: { type: "string", required: true },
  },
  indexes: {
    byCamera: {
      pk: { field: "pk", composite: ["tenant", "site", "asset", "camera"] },
      sk: { field: "sk", composite: ["timestamp"] },
    },
  },
});

export type Snapshot = EntityItem<typeof SnapshotEntity>;

/**
 * Pointer to the latest snapshot for each camera.
 * Updated transactionally with each snapshot write.
 *
 * Key design:
 * - PK: LATESTSNAPSHOT#{tenant} (enables batch query for all cameras in tenant)
 * - SK: #{site}#{asset}#{camera} (supports begins_with for optional site/camera filtering)
 *
 * Query patterns:
 * - All cameras for tenant: query({ tenant })
 * - All cameras at site: query({ tenant, site })
 * - Specific camera: query({ tenant, site, asset, camera })
 */
export const LatestSnapshotEntity = new Entity({
  model: {
    service: "a3s-media",
    entity: "LatestSnapshot",
    version: "1",
  },
  attributes: {
    tenant: tenantIdAttribute,
    site: siteIdAttribute,
    asset: assetIdAttribute,
    camera: cameraIdAttribute,
    timestamp: dateAttribute, // Latest snapshot timestamp
    snapshotKey: { type: "string", required: true }, // S3 key
    updatedAt: { type: "string", required: true },
  },
  indexes: {
    byTenant: {
      pk: { field: "pk", composite: ["tenant"] },
      sk: { field: "sk", composite: ["site", "asset", "camera"] },
    },
  },
});

export type LatestSnapshot = EntityItem<typeof LatestSnapshotEntity>;

export const CLIP_SOURCES = ["lazy", "monitored"] as const;
export type ClipSource = (typeof CLIP_SOURCES)[number];

/**
 * ElectroDB entity for clip metadata.
 *
 * Key design:
 * - PK: CLIP#{tenant}#{site}#{asset}#{camera} (camera identifier)
 * - SK: {ISO8601_start_time} (enables time-range queries with BETWEEN)
 *
 * Note: startTime is stored as ISO8601 string (not Date) because it's a composite
 * attribute in the sort key, and ElectroDB requires composite attributes to be
 * primitive types that can be used directly in DynamoDB key conditions.
 */
export const ClipEntity = new Entity({
  model: {
    service: "a3s-media",
    entity: "Clip",
    version: "1",
  },
  attributes: {
    tenant: tenantIdAttribute,
    site: siteIdAttribute,
    asset: assetIdAttribute,
    camera: cameraIdAttribute,
    startTime: dateAttribute,
    durationMs: { type: "number", required: true },
    source: { type: CLIP_SOURCES }, // absent on pre-existing clips
    clipKey: { type: "string" }, // S3 key for MP4 (null when video is on-device only)
    thumbnailKey: { type: "string" }, // S3 key for thumbnail (null until generated)
    edgeUrl: { type: "string" }, // Edge device URL for lazy fetch (null when video is in S3)
    createdAt: { type: "string", required: true, readOnly: true },
    updatedAt: { type: "string", required: true },
  },
  indexes: {
    byCamera: {
      pk: { field: "pk", composite: ["tenant", "site", "asset", "camera"] },
      sk: { field: "sk", composite: ["startTime"] },
    },
  },
});

export type Clip = EntityItem<typeof ClipEntity>;

/**
 * Timelapse export job lifecycle states.
 *
 * `queued`     — job persisted; waiting for the renderer worker
 * `processing` — renderer is downloading frames / running ffmpeg
 * `ready`      — MP4 is uploaded; status response includes the signed URL
 * `failed`     — rendering failed; `failureReason` is populated
 */
export const TIMELAPSE_VIDEO_JOB_STATUSES = ["queued", "processing", "ready", "failed"] as const;
export type TimelapseVideoJobStatus = (typeof TIMELAPSE_VIDEO_JOB_STATUSES)[number];

/**
 * ElectroDB entity for asynchronous timelapse export jobs.
 *
 * Key design:
 * - PK: TIMELAPSEJOB#{tenant}#{site}#{asset}#{camera} (camera identifier)
 * - SK: {windowKey} where windowKey is a deterministic identity for the
 *       camera-time-derived UTC window used by both viewer and exporter.
 *
 * `windowKey` is built from
 * `cameraTimezone|hourly(requestedStartTime)|hourly(requestedEndTime)` so that:
 * - requests within the same camera-local hour reuse the same render
 * - distinct ranges produce distinct rows even when the camera matches
 * - changing the camera timezone invalidates the cached video, because the
 *   underlying UTC inclusion window changes too
 *
 * Persisting both the requested timestamps and the resolved UTC window makes
 * later renders deterministic regardless of the requester's wall-clock.
 */
export const TimelapseVideoJobEntity = new Entity({
  model: {
    service: "a3s-media",
    entity: "TimelapseVideoJob",
    version: "1",
  },
  attributes: {
    tenant: tenantIdAttribute,
    site: siteIdAttribute,
    asset: assetIdAttribute,
    camera: cameraIdAttribute,
    /** Deterministic window identity (cameraTimezone|hourlyStart|hourlyEnd). */
    windowKey: { type: "string", required: true },
    cameraTimezone: { type: "string", required: true },
    requestedStartTime: dateAttribute,
    requestedEndTime: dateAttribute,
    /** UTC window resolved from camera-local boundaries — the renderer queries this. */
    rangeStartUtc: dateAttribute,
    rangeEndUtc: dateAttribute,
    status: { type: TIMELAPSE_VIDEO_JOB_STATUSES, required: true },
    /** S3 key for the rendered MP4 (set when status === "ready"). */
    videoKey: { type: "string" },
    /** Human-readable reason (set when status === "failed"). */
    failureReason: { type: "string" },
    /**
     * UTC timestamp at which the rendered MP4 expires and is eligible for
     * cleanup. Set when the job transitions to `ready` (default 7 days
     * after completion) and removed when the row is cleaned up or reset
     * to any non-ready state. Used by the periodic cleanup worker to find
     * jobs whose S3 output should be deleted.
     */
    expiresAt: { type: "string" },
    createdAt: { type: "string", required: true, readOnly: true },
    updatedAt: { type: "string", required: true },
  },
  indexes: {
    byCamera: {
      pk: { field: "pk", composite: ["tenant", "site", "asset", "camera"] },
      sk: { field: "sk", composite: ["windowKey"] },
    },
    /**
     * Newest-first lookup path for per-camera request timestamps.
     *
     * Used by the timelapse one-hour re-request guard to find the latest
     * successful (`status === "ready"`) request by `createdAt` without
     * relying on `windowKey` lexical order.
     */
    byCreatedAt: {
      index: "gsi1",
      pk: { field: "gsi1pk", composite: ["tenant", "site", "asset", "camera"] },
      sk: { field: "gsi1sk", composite: ["createdAt"] },
    },
  },
});

export type TimelapseVideoJob = EntityItem<typeof TimelapseVideoJobEntity>;

/**
 * ElectroDB service containing all media entities.
 */
export type MediaDb = Service<{
  Clip: typeof ClipEntity;
  Snapshot: typeof SnapshotEntity;
  LatestSnapshot: typeof LatestSnapshotEntity;
  TimelapseVideoJob: typeof TimelapseVideoJobEntity;
}>;

/**
 * Create a MediaDb instance with the given table and DynamoDB client.
 */
export function makeMediaDb(deps: { table: string; client: DynamoDBClient }): MediaDb {
  return new Service(
    {
      Clip: ClipEntity,
      Snapshot: SnapshotEntity,
      LatestSnapshot: LatestSnapshotEntity,
      TimelapseVideoJob: TimelapseVideoJobEntity,
    },
    {
      table: deps.table,
      client: DynamoDBDocumentClient.from(deps.client),
    },
  );
}
